import { useEffect, useRef } from 'react';
import type { ShareRow, ShareState } from '#shared/types';
import { useApiResource, useSubResource } from '#client/hooks/useResourceItem';
import { ENDPOINT } from '#client/lib/endpoints';
import { RESOURCE } from '#client/lib/routes';

/** Four seconds: fast enough to feel live, slow enough not to be a poll storm. */
const POLL_MS = 4_000;

/**
 * How long to keep asking.
 *
 * A `read` receipt requires the patient to have read receipts switched on, and
 * many do not — so a spinner that waits for `read` waits forever. The wall-clock
 * budget is what closes that: the screen simply stops updating at whatever the
 * last honest answer was, which is usually `delivered`, and a manual refresh
 * always works.
 */
const POLL_BUDGET_MS = 180_000;

export interface ShareView {
  state: ShareState | undefined;
  shares: ShareRow[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * The patient screen's view of WhatsApp: can this be sent, and what happened to
 * what was already sent.
 *
 * Two requests rather than one because they answer different questions and have
 * different lifetimes — readiness is derived on every read, while the history is
 * an append-only log. They are fetched together here so the button and the card
 * cannot disagree about the state of the same visit.
 *
 * There is no websocket and no React Query. Delivery status arrives by polling,
 * exactly as the importer already does for a long-running commit.
 */
export function useShareState(patientId: string | undefined): ShareView {
  const state = useApiResource<ShareState>(
    patientId === undefined ? undefined : ENDPOINT.whatsappPatient(patientId),
  );
  const shares = useSubResource<ShareRow[]>(RESOURCE.patients, patientId, 'shares');

  const startedAt = useRef<number | null>(null);

  // Anything not terminal is worth another look. `delivered` still is, because
  // a `read` may follow — the budget above is what stops that being forever.
  const pending = (shares.data ?? []).some(
    (share) => share.status !== 'read' && share.status !== 'failed',
  );

  const refreshState = state.refresh;
  const refreshShares = shares.refresh;

  useEffect(() => {
    if (patientId === undefined || !pending) {
      startedAt.current = null;
      return;
    }

    startedAt.current ??= Date.now();

    const timer = window.setInterval(() => {
      if (Date.now() - (startedAt.current ?? 0) > POLL_BUDGET_MS) {
        window.clearInterval(timer);
        return;
      }
      // A backgrounded tab polls nothing. The receipts are still there when the
      // operator comes back, and the next tick picks them up.
      if (document.visibilityState !== 'visible') return;

      refreshState();
      refreshShares();
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [patientId, pending, refreshState, refreshShares]);

  return {
    state: state.data ?? undefined,
    shares: shares.data ?? [],
    loading: state.loading,
    error: state.error ?? shares.error ?? null,
    refresh: () => {
      refreshState();
      refreshShares();
    },
  };
}
